import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarSearch } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";

export const DateBackfillPanel = ({ isAdmin, onFinished }) => {
  const [job, setJob] = useState(null);
  const wasRunning = useRef(false);
  const load = useCallback(() => api.get("/leads/dates/backfill").then((r) => setJob(r.data)).catch((e) => console.warn("backfill", e)), []);
  useEffect(() => { load(); }, [load]);

  const running = job?.status === "running";
  useEffect(() => {
    if (wasRunning.current && !running && job) {
      toast.success(`Found post dates for ${job.found} of ${job.done} leads`);
      onFinished();
    }
    wasRunning.current = running;
    if (!running) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load, job, onFinished]);

  const start = async () => {
    try {
      const { data } = await api.post("/leads/dates/backfill");
      setJob(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!job || (!running && !job.remaining && job.status === "idle")) return null;
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="date-backfill-panel">
      <p className="font-mono-label text-[10px] text-slate-500 mb-2 flex items-center gap-1.5"><CalendarSearch className="h-3.5 w-3.5 text-blue-600" />Missing post dates</p>
      {running ? (
        <>
          <p className="text-sm text-slate-700" data-testid="date-backfill-progress">Checking {job.done} of {job.total} leads · {job.found} dates found</p>
          <Progress value={pct} className="h-2 mt-3" />
        </>
      ) : (
        <>
          <p className="text-sm text-slate-700" data-testid="date-backfill-remaining">
            {job.remaining ? `${job.remaining} lead${job.remaining === 1 ? "" : "s"} without a post date.` : "All leads checked."}
            {job.status === "done" && ` Last run found ${job.found} of ${job.done}.`}
            {job.remaining > 0 && " A deeper search checks reviews, social posts, forums and news for each one."}
          </p>
          {job.status === "failed" && <p className="text-xs text-red-600 mt-1" data-testid="date-backfill-error">{job.error}</p>}
          {job.remaining > 0 && (isAdmin ? (
            <Button onClick={start} className="mt-3 w-full rounded-full bg-blue-700 hover:bg-blue-800" data-testid="date-backfill-start">
              Dig deeper for post dates
            </Button>
          ) : (
            <p className="text-xs text-slate-400 mt-1">Ask the admin to run the date lookup.</p>
          ))}
        </>
      )}
    </div>
  );
};
